import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import LeafletMap from "@/components/LeafletMap";
import TourneesTabs from "@/components/TourneesTabs";
import RouteEditorModal from "@/components/RouteEditorModal";
import {
  Plus, Trash2, Zap, MapPin, Pencil, Bus, User, Users, Clock, Route as RouteIcon,
  Copy, CheckCircle2, Send, Sparkles, Calendar, School, LayoutList, LayoutGrid, Map as MapIcon, CalendarDays, RefreshCw, ChevronRight,
} from "lucide-react";
import {
  PageShell, PageHeader, StatCard, StatGrid, StatusBadge, ActionButton, SectionCard,
  EmptyState, SearchBar, FilterTabs, KeyValueRow,
} from "@/components/ds";

// ---- Constants -------------------------------------------------------------
const STATUS_META = {
  draft:      { label: "Brouillon",  tone: "neutral" },
  optimized:  { label: "Optimisée",  tone: "info" },
  validated:  { label: "Validée",    tone: "yellow" },
  active:     { label: "Active",     tone: "success" },
  completed:  { label: "Terminée",   tone: "muted" },
};

const TYPE_META = {
  pickup:     { label: "Ramassage",       short: "Matin",        icon: "🌅" },
  dropoff:    { label: "Dépose",          short: "Retour",       icon: "🏠" },
  morning:    { label: "Ramassage matin", short: "Matin",        icon: "🌅" },
  midday:     { label: "Retour midi",     short: "Midi",         icon: "🍴" },
  afternoon:  { label: "Retour après-midi", short: "Après-midi", icon: "🏠" },
  wednesday:  { label: "Mercredi",        short: "Mercredi",     icon: "🎒" },
  special:    { label: "Spécial",         short: "Spécial",      icon: "✨" },
};

const VIEW_OPTIONS = [
  { key: "list",     label: "Liste",     icon: LayoutList },
  { key: "cards",    label: "Cartes",    icon: LayoutGrid },
  { key: "planning", label: "Planning",  icon: CalendarDays },
  { key: "schools",  label: "Par école", icon: School },
];

// ---- Helpers ---------------------------------------------------------------
const stopsChildrenCount = (r) => (r.stops || []).reduce((a, s) => a + (s.children_ids?.length || 0), 0);
const safeStatus = (r) => r.status || "draft";

// ---- Main page -------------------------------------------------------------
export default function AdminRoutes() {
  const [routes, setRoutes] = useState([]);
  const [children, setChildren] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);   // null | "new" | <route>
  const [loading, setLoading] = useState(false);

  // filters
  const [view, setView] = useState("list");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [schoolFilter, setSchoolFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [assignFilter, setAssignFilter] = useState("all"); // all|assigned|unassigned

  const load = async () => {
    setLoading(true);
    try {
      const [r, c, v, d] = await Promise.all([
        api.get("/routes"),
        api.get("/children").catch(() => ({ data: [] })),
        api.get("/vehicles").catch(() => ({ data: [] })),
        api.get("/users?role=driver&active_only=true").catch(() => ({ data: [] })),
      ]);
      setRoutes(r.data);
      setChildren(c.data);
      setVehicles(v.data);
      setDrivers(d.data);
      if (r.data.length && !selected) setSelected(r.data[0]);
      else if (selected) {
        const refreshed = r.data.find((x) => x.id === selected.id);
        if (refreshed) setSelected(refreshed);
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- index by id ---------------------------------------------------------
  const vehicleById = useMemo(() => Object.fromEntries(vehicles.map((v) => [v.id, v])), [vehicles]);
  const driverById = useMemo(() => Object.fromEntries(drivers.map((d) => [d.id, d])), [drivers]);
  const childById = useMemo(() => Object.fromEntries(children.map((c) => [c.id, c])), [children]);

  // ---- school options -----------------------------------------------------
  const schoolOptions = useMemo(() => {
    const set = new Map();
    routes.forEach((r) => {
      const k = (r.school_address || "—").trim();
      set.set(k, (set.get(k) || 0) + 1);
    });
    return Array.from(set.entries()).map(([k, n]) => ({ key: k, count: n }));
  }, [routes]);

  // ---- filtered routes -----------------------------------------------------
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return routes.filter((r) => {
      if (statusFilter !== "all" && safeStatus(r) !== statusFilter) return false;
      if (schoolFilter !== "all" && (r.school_address || "—") !== schoolFilter) return false;
      if (typeFilter !== "all" && r.type !== typeFilter) return false;
      if (dateFilter && r.date !== dateFilter) return false;
      if (assignFilter === "assigned" && !(r.assigned_vehicle_id || r.assigned_driver_id)) return false;
      if (assignFilter === "unassigned" && (r.assigned_vehicle_id || r.assigned_driver_id)) return false;
      if (!q) return true;
      const hay = `${r.name} ${r.school_address || ""} ${r.date || ""} ${vehicleById[r.assigned_vehicle_id]?.plate || ""} ${driverById[r.assigned_driver_id]?.name || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [routes, search, statusFilter, schoolFilter, typeFilter, dateFilter, assignFilter, vehicleById, driverById]);

  // ---- KPI strip -----------------------------------------------------------
  const kpis = useMemo(() => {
    const byStatus = { draft: 0, optimized: 0, validated: 0, active: 0, completed: 0 };
    let totalKids = 0, totalKm = 0;
    routes.forEach((r) => {
      byStatus[safeStatus(r)] = (byStatus[safeStatus(r)] || 0) + 1;
      totalKids += stopsChildrenCount(r);
      totalKm += Number(r.metrics?.total_distance_km || 0);
    });
    return { total: routes.length, ...byStatus, totalKids, totalKm: Math.round(totalKm * 10) / 10 };
  }, [routes]);

  // ---- action handlers ----------------------------------------------------
  const optimize = async (id) => {
    try {
      const { data } = await api.post(`/routes/${id}/optimize`);
      const before = data.metrics_before, after = data.metrics;
      const gainKm = before && after ? (before.total_distance_km - after.total_distance_km).toFixed(2) : 0;
      toast.success(`Tournée optimisée — ${after.total_distance_km} km (gain ${gainKm} km)`);
      setRoutes((rs) => rs.map((r) => (r.id === id ? data : r)));
      setSelected(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const autoAssignVehicle = async (id) => {
    try {
      const { data } = await api.post(`/routes/${id}/auto-assign-vehicle`);
      toast.success(`Bus ${data.vehicle.plate} affecté (capacité ${data.vehicle.capacity} pour ${data.children_count} passagers)`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const autoAssignDriver = async (id) => {
    try {
      const { data } = await api.post(`/routes/${id}/auto-assign-driver`);
      toast.success(`Chauffeur ${data.driver.name || data.driver.email} affecté`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const generateSchedule = async (id) => {
    try {
      const { data } = await api.post(`/routes/${id}/generate-schedule`);
      toast.success(`Horaires générés (${data.stops?.length || 0} arrêts)`);
      setRoutes((rs) => rs.map((r) => (r.id === id ? data : r)));
      setSelected(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const validate = async (id) => {
    try {
      const { data } = await api.post(`/routes/${id}/validate`);
      toast.success("Tournée validée");
      setRoutes((rs) => rs.map((r) => (r.id === id ? { ...r, ...data } : r)));
      setSelected((cur) => (cur?.id === id ? { ...cur, ...data } : cur));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const publish = async (id) => {
    try {
      const { data } = await api.post(`/routes/${id}/publish`);
      toast.success("Tournée publiée au chauffeur");
      setRoutes((rs) => rs.map((r) => (r.id === id ? { ...r, ...data } : r)));
      setSelected((cur) => (cur?.id === id ? { ...cur, ...data } : cur));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/routes/${id}/duplicate`);
      toast.success(`Tournée dupliquée : ${data.name}`);
      load();
      setSelected(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const remove = async (id) => {
    if (!confirm("Supprimer cette tournée ?")) return;
    try {
      await api.delete(`/routes/${id}`);
      toast.success("Tournée supprimée");
      setSelected(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const resetFilters = () => {
    setSearch(""); setStatusFilter("all"); setSchoolFilter("all");
    setTypeFilter("all"); setDateFilter(""); setAssignFilter("all");
  };

  // ---- render --------------------------------------------------------------
  return (
    <PageShell testId="admin-routes-page">
      <TourneesTabs />
      <PageHeader
        eyebrow="Planification scolaire"
        title="Tournées scolaires"
        subtitle="Créez, optimisez, validez et publiez les tournées de ramassage et de retour. École → arrêts → élèves → bus → chauffeur."
        actions={
          <>
            <ActionButton variant="ghost" icon={loading ? null : RefreshCw} loading={loading} onClick={load} testId="refresh-routes-btn">
              Rafraîchir
            </ActionButton>
            <ActionButton variant="primary" size="lg" icon={Plus} onClick={() => setEditing("new")} testId="add-route-btn">
              Nouvelle tournée
            </ActionButton>
          </>
        }
      />

      {/* KPI strip */}
      <StatGrid cols={6} className="mb-5" testId="routes-kpi">
        <StatCard testId="kpi-total" icon={RouteIcon} label="Total" value={kpis.total} accent />
        <StatCard testId="kpi-draft" icon={Pencil} label="Brouillons" value={kpis.draft} />
        <StatCard testId="kpi-validated" icon={CheckCircle2} label="Validées" value={kpis.validated} tone={kpis.validated > 0 ? "success" : "default"} />
        <StatCard testId="kpi-active" icon={Send} label="Actives" value={kpis.active} tone={kpis.active > 0 ? "navy" : "default"} />
        <StatCard testId="kpi-kids" icon={Users} label="Élèves transportés" value={kpis.totalKids} />
        <StatCard testId="kpi-km" icon={MapPin} label="Distance cumulée" value={`${kpis.totalKm} km`} />
      </StatGrid>

      {/* Filters bar */}
      <SectionCard tone="navy" icon={Sparkles} title="Filtres" className="mb-5" bodyClassName="p-3 bg-white">
        <div className="grid lg:grid-cols-12 gap-3 items-center">
          <div className="lg:col-span-4">
            <SearchBar value={search} onChange={setSearch} placeholder="Nom, école, bus, chauffeur…" testId="routes-search" />
          </div>
          <div className="lg:col-span-3">
            <select data-testid="filter-school" value={schoolFilter} onChange={(e) => setSchoolFilter(e.target.value)}
              className="w-full border border-zinc-900 px-3 py-2 text-sm font-mono bg-white">
              <option value="all">Toutes les écoles</option>
              {schoolOptions.map((s) => <option key={s.key} value={s.key}>{`${s.key} (${s.count})`}</option>)}
            </select>
          </div>
          <div className="lg:col-span-2">
            <select data-testid="filter-type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full border border-zinc-900 px-3 py-2 text-sm font-mono bg-white">
              <option value="all">Tous types</option>
              {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div className="lg:col-span-2">
            <input type="date" data-testid="filter-date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}
              className="w-full border border-zinc-900 px-3 py-2 text-sm font-mono bg-white" />
          </div>
          <div className="lg:col-span-1">
            <button onClick={resetFilters} className="w-full text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-900 underline">
              Réinit.
            </button>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
          <FilterTabs
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { key: "all",       label: "Toutes",     count: kpis.total },
              { key: "draft",     label: "Brouillon",  count: kpis.draft },
              { key: "optimized", label: "Optimisée",  count: kpis.optimized },
              { key: "validated", label: "Validée",    count: kpis.validated },
              { key: "active",    label: "Active",     count: kpis.active },
              { key: "completed", label: "Terminée",   count: kpis.completed },
            ]}
            testIdPrefix="filter-status"
            size="sm"
          />
          <FilterTabs
            value={assignFilter}
            onChange={setAssignFilter}
            options={[
              { key: "all",        label: "Affectation : tout" },
              { key: "assigned",   label: "Affectée" },
              { key: "unassigned", label: "Sans affectation" },
            ]}
            tone="navy"
            size="sm"
            testIdPrefix="filter-assign"
          />
        </div>
      </SectionCard>

      {/* View switcher */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <FilterTabs
          value={view}
          onChange={setView}
          options={VIEW_OPTIONS.map((v) => ({ key: v.key, label: v.label }))}
          testIdPrefix="view"
        />
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500" data-testid="routes-counter">
          {filtered.length} / {routes.length} tournée{routes.length > 1 ? "s" : ""}
        </div>
      </div>

      {/* Views */}
      {filtered.length === 0 ? (
        <SectionCard tone="white" testId="routes-empty">
          <EmptyState
            icon={RouteIcon}
            title="Aucune tournée"
            description={routes.length === 0 ? "Créez votre première tournée scolaire." : "Aucune tournée ne correspond aux filtres."}
            action={
              <ActionButton variant="primary" icon={Plus} onClick={() => setEditing("new")} testId="empty-create-btn">
                Créer une tournée
              </ActionButton>
            }
          />
        </SectionCard>
      ) : view === "list" ? (
        <ListAndDetail
          routes={filtered}
          selected={selected}
          setSelected={setSelected}
          vehicleById={vehicleById}
          driverById={driverById}
          childById={childById}
          onEdit={(r) => setEditing(r)}
          onOptimize={optimize}
          onAutoAssignVehicle={autoAssignVehicle}
          onAutoAssignDriver={autoAssignDriver}
          onGenerateSchedule={generateSchedule}
          onValidate={validate}
          onPublish={publish}
          onDuplicate={duplicate}
          onDelete={remove}
        />
      ) : view === "cards" ? (
        <CardsView
          routes={filtered}
          vehicleById={vehicleById}
          driverById={driverById}
          onOpen={(r) => { setSelected(r); setView("list"); }}
        />
      ) : view === "planning" ? (
        <PlanningView routes={filtered} vehicleById={vehicleById} driverById={driverById} onOpen={(r) => { setSelected(r); setView("list"); }} />
      ) : (
        <SchoolsView routes={filtered} onOpen={(r) => { setSelected(r); setView("list"); }} />
      )}

      {editing && (
        <RouteEditorModal
          initial={editing === "new" ? null : editing}
          children={children}
          onClose={() => setEditing(null)}
          onSaved={(saved) => { setEditing(null); setSelected(saved); load(); }}
        />
      )}
    </PageShell>
  );
}

// =========================================================================
// VIEW: list + detail (default)
// =========================================================================
function ListAndDetail({
  routes, selected, setSelected,
  vehicleById, driverById, childById,
  onEdit, onOptimize, onAutoAssignVehicle, onAutoAssignDriver, onGenerateSchedule,
  onValidate, onPublish, onDuplicate, onDelete,
}) {
  return (
    <div className="grid lg:grid-cols-12 gap-4">
      {/* Left: list */}
      <div className="lg:col-span-5">
        <SectionCard tone="navy" icon={RouteIcon} title={`Tournées (${routes.length})`} bodyClassName="max-h-[78vh] overflow-y-auto divide-y divide-zinc-200">
          {routes.map((r) => {
            const active = selected?.id === r.id;
            const t = TYPE_META[r.type] || TYPE_META.pickup;
            const status = safeStatus(r);
            const sm = STATUS_META[status];
            return (
              <button
                key={r.id}
                data-testid={`route-item-${r.id}`}
                onClick={() => setSelected(r)}
                className={`w-full text-left p-3 hover:bg-zinc-50 transition-colors ${active ? "bg-[#FFC60A]/20 border-l-4 border-l-[#FFC60A]" : "border-l-4 border-l-transparent"}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-display font-bold text-sm tracking-tight truncate">{r.name}</div>
                    <div className="text-[10px] font-mono text-zinc-500 truncate mt-0.5">{r.school_address}</div>
                  </div>
                  <StatusBadge tone={sm.tone} label={sm.label} size="sm" withDot={false} />
                </div>
                <div className="mt-2 flex items-center gap-2 flex-wrap text-[10px] font-mono uppercase tracking-widest text-zinc-600">
                  <span>{t.icon} {t.short}</span>
                  <span>· <Calendar size={9} className="inline" /> {r.date}</span>
                  <span>· <MapPin size={9} className="inline" /> {r.stops?.length || 0}</span>
                  <span>· <Users size={9} className="inline" /> {stopsChildrenCount(r)}</span>
                </div>
                <div className="mt-1.5 flex items-center gap-2 flex-wrap text-[10px] font-mono text-zinc-700">
                  {r.assigned_vehicle_id ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 border border-emerald-300 text-emerald-800">
                      <Bus size={9} /> {vehicleById[r.assigned_vehicle_id]?.plate || "Bus"}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-zinc-100 border border-zinc-300 text-zinc-500">
                      <Bus size={9} /> Sans bus
                    </span>
                  )}
                  {r.assigned_driver_id ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 border border-emerald-300 text-emerald-800">
                      <User size={9} /> {driverById[r.assigned_driver_id]?.name || "Chauffeur"}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-zinc-100 border border-zinc-300 text-zinc-500">
                      <User size={9} /> Sans chauffeur
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </SectionCard>
      </div>

      {/* Right: detail */}
      <div className="lg:col-span-7">
        {selected ? (
          <RouteDetail
            route={selected}
            vehicle={vehicleById[selected.assigned_vehicle_id]}
            driver={driverById[selected.assigned_driver_id]}
            childById={childById}
            onEdit={() => onEdit(selected)}
            onOptimize={() => onOptimize(selected.id)}
            onAutoAssignVehicle={() => onAutoAssignVehicle(selected.id)}
            onAutoAssignDriver={() => onAutoAssignDriver(selected.id)}
            onGenerateSchedule={() => onGenerateSchedule(selected.id)}
            onValidate={() => onValidate(selected.id)}
            onPublish={() => onPublish(selected.id)}
            onDuplicate={() => onDuplicate(selected.id)}
            onDelete={() => onDelete(selected.id)}
          />
        ) : (
          <SectionCard tone="white">
            <EmptyState icon={Sparkles} title="Sélectionnez une tournée" description="Cliquez une carte à gauche pour afficher les détails." />
          </SectionCard>
        )}
      </div>
    </div>
  );
}

// =========================================================================
// Detail panel
// =========================================================================
function RouteDetail({ route, vehicle, driver, childById, onEdit, onOptimize, onAutoAssignVehicle, onAutoAssignDriver, onGenerateSchedule, onValidate, onPublish, onDuplicate, onDelete }) {
  const t = TYPE_META[route.type] || TYPE_META.pickup;
  const status = safeStatus(route);
  const sm = STATUS_META[status];
  const m = route.metrics || {};
  const waypoints = [
    ...(route.school_lat ? [{ lat: route.school_lat, lng: route.school_lng, address: `🏫 ${route.school_address || "École"}` }] : []),
    ...(route.stops || []).map((s, i) => ({ lat: s.lat, lng: s.lng, address: `📍 ${i + 1}. ${s.address}` })),
  ];

  const canValidate = ["draft", "optimized"].includes(status) && (route.stops?.length || 0) > 0;
  const canPublish = status === "validated" && route.assigned_vehicle_id && route.assigned_driver_id;

  return (
    <SectionCard
      testId="route-detail"
      tone="navy"
      icon={RouteIcon}
      title={route.name}
      actions={<StatusBadge tone={sm.tone} label={sm.label} size="sm" withDot={false} />}
    >
      {/* Sub-header */}
      <div className="p-3 border-b border-zinc-200 flex items-start justify-between flex-wrap gap-3 bg-zinc-50">
        <div className="min-w-0">
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">
            {t.icon} {t.label} · {route.date}
          </div>
          <div className="text-sm text-zinc-700 mt-1 flex items-center gap-1.5">
            <School size={13} className="text-zinc-500" />
            {route.school_address || "École non renseignée"}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <ActionButton size="sm" variant="ghost" icon={Pencil} onClick={onEdit} testId={`edit-route-${route.id}`}>Modifier</ActionButton>
          <ActionButton size="sm" variant="ghost" icon={Copy} onClick={onDuplicate} testId={`duplicate-route-${route.id}`}>Dupliquer</ActionButton>
          <ActionButton size="sm" variant="danger" icon={Trash2} onClick={onDelete} testId={`delete-route-${route.id}`}>
            <span className="sr-only">Supprimer</span>
          </ActionButton>
        </div>
      </div>

      {/* Action toolbar (lifecycle) */}
      <div className="p-3 border-b border-zinc-200 flex flex-wrap gap-1.5">
        <ActionButton size="sm" variant="ghost" icon={Zap} onClick={onOptimize} testId={`optimize-btn-${route.id}`}>Optimiser</ActionButton>
        <ActionButton size="sm" variant="ghost" icon={Clock} onClick={onGenerateSchedule} testId={`schedule-btn-${route.id}`}>Générer horaires</ActionButton>
        <ActionButton size="sm" variant="ghost" icon={Bus} onClick={onAutoAssignVehicle} testId={`assign-vehicle-btn-${route.id}`}>Affecter bus auto</ActionButton>
        <ActionButton size="sm" variant="ghost" icon={User} onClick={onAutoAssignDriver} testId={`assign-driver-btn-${route.id}`}>Affecter chauffeur</ActionButton>
        <ActionButton size="sm" variant="success" icon={CheckCircle2} onClick={onValidate} disabled={!canValidate} testId={`validate-btn-${route.id}`}>Valider</ActionButton>
        <ActionButton size="sm" variant="primary" icon={Send} onClick={onPublish} disabled={!canPublish} testId={`publish-btn-${route.id}`}>Publier au chauffeur</ActionButton>
      </div>

      {/* Map */}
      <div className="border-b border-zinc-200">
        <LeafletMap waypoints={waypoints} height={300} testId="route-map" />
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-0 divide-x divide-zinc-200 border-b border-zinc-200" data-testid="route-metrics">
        <MetricCell label="Distance" value={m.total_distance_km != null ? `${m.total_distance_km}` : "—"} unit="km" />
        <MetricCell label="Durée" value={m.total_duration_min != null ? `${m.total_duration_min}` : "—"} unit="min" />
        <MetricCell label="Heure école" value={m.school_eta || "—"} />
        <MetricCell label="Remplissage" value={m.occupancy_rate != null ? `${m.occupancy_rate}` : "—"} unit="%" accent />
        <MetricCell label="Passagers" value={m.children_count ?? stopsChildrenCount(route)} />
      </div>

      {/* Assignment + recap */}
      <div className="grid sm:grid-cols-2 gap-0 border-b border-zinc-200 divide-x divide-zinc-200">
        <div className="p-3">
          <KeyValueRow label="Bus" value={vehicle ? <span className="font-mono">{vehicle.plate} · {vehicle.model} · cap {vehicle.capacity}</span> : <span className="text-zinc-400">Non affecté</span>} />
          <KeyValueRow label="Chauffeur" value={driver ? <span>{driver.name} <span className="text-zinc-400 font-mono">({driver.email})</span></span> : <span className="text-zinc-400">Non affecté</span>} />
          {route.recurrence && route.recurrence !== "none" && (
            <KeyValueRow label="Récurrence" value={<span className="font-mono uppercase text-xs">{route.recurrence}{(route.recurrence_days || []).length ? ` · ${route.recurrence_days.join(", ")}` : ""}</span>} />
          )}
          {route.target_arrival_time && (
            <KeyValueRow label="Cible arrivée" value={<span className="font-mono">{route.target_arrival_time}</span>} />
          )}
        </div>
        <div className="p-3 bg-zinc-50">
          <KeyValueRow label="Créée le" value={route.created_at ? route.created_at.slice(0, 16).replace("T", " ") : "—"} />
          {route.validated_at && <KeyValueRow label="Validée le" value={route.validated_at.slice(0, 16).replace("T", " ")} />}
          {route.published_at && <KeyValueRow label="Publiée le" value={route.published_at.slice(0, 16).replace("T", " ")} />}
          {route.optimized_at && <KeyValueRow label="Optimisée le" value={route.optimized_at.slice(0, 16).replace("T", " ")} />}
        </div>
      </div>

      {/* Stops list */}
      <div className="p-3">
        <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500 mb-2">
          Ordre de passage — {route.stops?.length || 0} arrêt{(route.stops?.length || 0) > 1 ? "s" : ""}
        </div>
        {(route.stops || []).length === 0 ? (
          <EmptyState dense icon={MapPin} title="Aucun arrêt" description="Ouvrez « Modifier » pour ajouter des points de ramassage." />
        ) : (
          <ol className="border border-zinc-200 divide-y divide-zinc-200">
            {(route.stops || []).map((s, i) => {
              const isFirst = i === 0;
              const isLast = i === (route.stops.length - 1);
              const kids = (s.children_ids || []).map((id) => childById[id]).filter(Boolean);
              return (
                <li key={s.id || i} className="p-3 text-sm" data-testid={`stop-${i}`}>
                  <div className="flex items-start gap-3">
                    <span className={`w-8 h-8 shrink-0 flex items-center justify-center font-bold border border-zinc-900 font-display ${
                      isFirst ? "bg-emerald-500 text-white" : isLast ? "bg-red-500 text-white" : "bg-[#FFC60A]"
                    }`}>{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="font-semibold truncate">{s.address}</div>
                        <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-400">
                          {isFirst ? "Départ" : isLast && route.stops.length > 1 ? "Arrivée école" : "Intermédiaire"}
                        </span>
                      </div>
                      <div className="text-[10px] font-mono text-zinc-500 mt-1 flex items-center gap-3 flex-wrap">
                        <span><Users size={10} className="inline mr-0.5" /> {kids.length} passager{kids.length > 1 ? "s" : ""}</span>
                        {s.scheduled_time && <span><Clock size={10} className="inline mr-0.5" /> {s.scheduled_time}</span>}
                      </div>
                      {kids.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {kids.map((k) => (
                            <span key={k.id} className="text-[10px] font-mono px-1.5 py-0.5 bg-zinc-100 border border-zinc-300">
                              {k.first_name} {k.last_name?.[0] || ""}.
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </SectionCard>
  );
}

function MetricCell({ label, value, unit, accent }) {
  return (
    <div className={`p-3 ${accent ? "bg-[#FFC60A]" : "bg-white"}`}>
      <div className="text-[10px] font-mono tracking-[0.25em] uppercase opacity-70">{label}</div>
      <div className="font-display font-black text-2xl tracking-tighter mt-1 leading-none">
        {value}{unit && <span className="text-xs text-zinc-500 ml-1">{unit}</span>}
      </div>
    </div>
  );
}

// =========================================================================
// VIEW: cards grid
// =========================================================================
function CardsView({ routes, vehicleById, driverById, onOpen }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="cards-view">
      {routes.map((r) => {
        const t = TYPE_META[r.type] || TYPE_META.pickup;
        const status = safeStatus(r);
        const sm = STATUS_META[status];
        const m = r.metrics || {};
        const veh = vehicleById[r.assigned_vehicle_id];
        const drv = driverById[r.assigned_driver_id];
        return (
          <div key={r.id} className="border border-zinc-900 bg-white p-4 hover:shadow-[6px_6px_0_0_#18181b] transition-all" data-testid={`route-card-${r.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0">
                <div className="text-[9px] font-mono tracking-[0.3em] uppercase text-zinc-500">{t.icon} {t.short} · {r.date}</div>
                <div className="font-display font-bold text-base tracking-tight truncate mt-0.5">{r.name}</div>
                <div className="text-[10px] font-mono text-zinc-500 truncate">{r.school_address}</div>
              </div>
              <StatusBadge tone={sm.tone} label={sm.label} size="sm" withDot={false} />
            </div>
            <div className="grid grid-cols-2 gap-1 my-3 text-[10px] font-mono uppercase tracking-widest">
              <Pill icon={MapPin} value={`${r.stops?.length || 0} arrêts`} />
              <Pill icon={Users} value={`${m.children_count ?? stopsChildrenCount(r)} élèves`} />
              <Pill icon={RouteIcon} value={`${m.total_distance_km ?? "—"} km`} />
              <Pill icon={Clock} value={`${m.total_duration_min ?? "—"} min`} />
            </div>
            <div className="flex flex-wrap gap-1 mb-3 text-[10px] font-mono">
              {veh ? (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 border border-emerald-300 text-emerald-800">
                  <Bus size={9} /> {veh.plate}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-zinc-100 border border-zinc-300 text-zinc-500"><Bus size={9} /> Sans bus</span>
              )}
              {drv ? (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 border border-emerald-300 text-emerald-800">
                  <User size={9} /> {drv.name}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-zinc-100 border border-zinc-300 text-zinc-500"><User size={9} /> Sans chauffeur</span>
              )}
            </div>
            <ActionButton variant="ghost" size="sm" onClick={() => onOpen(r)} className="w-full justify-center" trailing={<ChevronRight size={11} />}>
              Ouvrir
            </ActionButton>
          </div>
        );
      })}
    </div>
  );
}

function Pill({ icon: Icon, value }) {
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-zinc-50 border border-zinc-200 text-zinc-700">
      <Icon size={10} /> {value}
    </span>
  );
}

// =========================================================================
// VIEW: planning by date
// =========================================================================
function PlanningView({ routes, vehicleById, driverById, onOpen }) {
  const byDate = useMemo(() => {
    const m = {};
    routes.forEach((r) => { (m[r.date] = m[r.date] || []).push(r); });
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b));
  }, [routes]);

  return (
    <div className="space-y-3" data-testid="planning-view">
      {byDate.map(([date, items]) => (
        <SectionCard key={date} tone="navy" icon={Calendar} title={`${date} · ${items.length} tournée${items.length > 1 ? "s" : ""}`}>
          <div className="divide-y divide-zinc-200">
            {items.map((r) => {
              const t = TYPE_META[r.type] || TYPE_META.pickup;
              const sm = STATUS_META[safeStatus(r)];
              const veh = vehicleById[r.assigned_vehicle_id];
              const drv = driverById[r.assigned_driver_id];
              const firstStop = (r.stops || [])[0];
              return (
                <button
                  key={r.id}
                  onClick={() => onOpen(r)}
                  className="w-full text-left p-3 hover:bg-zinc-50 transition-colors flex items-center gap-3 flex-wrap"
                  data-testid={`planning-row-${r.id}`}
                >
                  <div className="w-14 text-center shrink-0">
                    <div className="font-display font-black text-lg leading-none">{firstStop?.scheduled_time || "—"}</div>
                    <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">{t.short}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-sm truncate">{r.name}</div>
                    <div className="text-[10px] font-mono text-zinc-500 truncate">{r.school_address}</div>
                  </div>
                  <div className="text-[10px] font-mono text-zinc-600 hidden sm:flex items-center gap-2 flex-wrap">
                    <span><MapPin size={9} className="inline" /> {r.stops?.length || 0}</span>
                    <span>· <Users size={9} className="inline" /> {stopsChildrenCount(r)}</span>
                  </div>
                  <div className="text-[10px] font-mono hidden md:flex items-center gap-1.5 flex-wrap">
                    {veh ? <span className="px-1.5 py-0.5 bg-emerald-50 border border-emerald-300 text-emerald-800">{veh.plate}</span> : <span className="px-1.5 py-0.5 bg-zinc-100 text-zinc-500 border border-zinc-300">Sans bus</span>}
                    {drv ? <span className="px-1.5 py-0.5 bg-emerald-50 border border-emerald-300 text-emerald-800">{drv.name}</span> : <span className="px-1.5 py-0.5 bg-zinc-100 text-zinc-500 border border-zinc-300">Sans chauffeur</span>}
                  </div>
                  <StatusBadge tone={sm.tone} label={sm.label} size="sm" withDot={false} />
                  <ChevronRight size={14} className="text-zinc-400 shrink-0" />
                </button>
              );
            })}
          </div>
        </SectionCard>
      ))}
    </div>
  );
}

// =========================================================================
// VIEW: by school
// =========================================================================
function SchoolsView({ routes, onOpen }) {
  const bySchool = useMemo(() => {
    const m = {};
    routes.forEach((r) => {
      const k = r.school_address || "École non renseignée";
      (m[k] = m[k] || []).push(r);
    });
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b));
  }, [routes]);

  return (
    <div className="grid lg:grid-cols-2 gap-3" data-testid="schools-view">
      {bySchool.map(([school, items]) => {
        const kids = items.reduce((a, r) => a + stopsChildrenCount(r), 0);
        return (
          <SectionCard key={school} tone="yellow" icon={School} title={`${school}`}>
            <div className="px-3 pt-3 grid grid-cols-3 gap-0 divide-x divide-zinc-200 border-b border-zinc-200">
              <div className="text-center py-1.5"><div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Tournées</div><div className="font-display font-black text-xl">{items.length}</div></div>
              <div className="text-center py-1.5"><div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Arrêts</div><div className="font-display font-black text-xl">{items.reduce((a, r) => a + (r.stops?.length || 0), 0)}</div></div>
              <div className="text-center py-1.5"><div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Élèves</div><div className="font-display font-black text-xl">{kids}</div></div>
            </div>
            <div className="divide-y divide-zinc-200">
              {items.map((r) => {
                const sm = STATUS_META[safeStatus(r)];
                const t = TYPE_META[r.type] || TYPE_META.pickup;
                return (
                  <button
                    key={r.id}
                    onClick={() => onOpen(r)}
                    className="w-full text-left p-3 hover:bg-zinc-50 transition-colors flex items-center justify-between gap-3"
                    data-testid={`school-row-${r.id}`}
                  >
                    <div className="min-w-0">
                      <div className="font-display font-bold text-sm truncate">{r.name}</div>
                      <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{t.icon} {t.short} · {r.date} · {r.stops?.length || 0} arrêts</div>
                    </div>
                    <StatusBadge tone={sm.tone} label={sm.label} size="sm" withDot={false} />
                  </button>
                );
              })}
            </div>
          </SectionCard>
        );
      })}
    </div>
  );
}
